/**
 * Catalogue → Extractor Jobs: every extraction job, across every brand.
 *
 * Live rather than polled. The extractor writes `extraction_jobs/{jobId}` with
 * the Admin SDK and the runner updates the same doc as it goes (memo §12,
 * locked answer 3), so an `onSnapshot` shows a status pill moving
 * queued → running → completed with no work on this side.
 *
 * Cross-brand rather than per-brand: an operator watching a run has just come
 * from one brand page, but the question the morning after — "did anything fail
 * overnight?" — spans all of them, and a per-brand table cannot answer it.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { ImportJobResultButton } from './ImportJobResultButton';
import {
  cancelExtractorJob,
  deleteExtractorJobs,
  extractorErrorMessage,
  forceCloseExtractorJob,
  getExtractorJobResult,
  subscribeToExtractorJobs,
} from '../../services/extractorService';
import {
  JOB_STATUSES,
  filterJobs,
  formatDuration,
  isCancellable,
  isJobDeletable,
  jobDurationMs,
  jobErrorSnippet,
  jobResultCount,
  sortJobs,
  statusClasses,
  stuckJobReason,
  stuckReasonLabel,
  type ExtractionJobStatus,
  type ExtractorJobDoc,
} from '../../services/extractorJobs';
import type { Timestamp } from 'firebase/firestore';

type StatusFilter = ExtractionJobStatus | 'all' | 'active';

interface ExtractorJobsPageProps {
  /** Pre-filter to the brand the operator just ran, when arriving from one. */
  initialBrandId?: string;
  onBrandConsumed?: () => void;
  /** Jumps back to the brand editor for a row. */
  onOpenBrand?: (brandId: string) => void;
  /** A run's result was imported — for a host that shows reference counts. */
  onImported?: () => void;
}

function formatTime(value: Timestamp | undefined): string {
  if (!value) return '—';
  return value.toDate().toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ExtractorJobsPage({
  initialBrandId,
  onBrandConsumed,
  onOpenBrand,
  onImported,
}: ExtractorJobsPageProps) {
  const [jobs, setJobs] = useState<ExtractorJobDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [brandId, setBrandId] = useState<string>(initialBrandId ?? '');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busyJobId, setBusyJobId] = useState<string | null>(null);
  /* Age filter, in days. 0 is "any age" — the list's own default. */
  const [maxAgeDays, setMaxAgeDays] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Rows the confirmation modal is asking about; null when it is closed. */
  const [pendingDelete, setPendingDelete] = useState<ExtractorJobDoc[] | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** The row the force-close confirmation is asking about; null when closed. */
  const [pendingForceClose, setPendingForceClose] = useState<ExtractorJobDoc | null>(null);
  const [forceClosing, setForceClosing] = useState(false);
  /*
   * Staleness is a function of the clock, and nothing in a stuck row changes to
   * re-render it — the whole point is that the doc stopped moving. Without a
   * tick, a job that crosses the threshold while the tab is open never grows
   * its button. A minute is far finer than either threshold.
   */
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (initialBrandId) {
      setBrandId(initialBrandId);
      onBrandConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBrandId]);

  useEffect(() => {
    const unsubscribe = subscribeToExtractorJobs(
      (next) => {
        setJobs(next);
        setError(null);
        setLoading(false);
      },
      (err) => {
        /*
         * The likeliest cause is the `extraction_jobs` read rule not being
         * deployed yet — rules live in the watchlock repo, and a missing one
         * reaches here as a permission error rather than an empty list.
         */
        setError(err.message);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  const brandIds = useMemo(
    () =>
      Array.from(
        new Set(jobs.map((j) => j.brandId).filter((b): b is string => typeof b === 'string')),
      ).sort(),
    [jobs],
  );

  const visible = useMemo(
    () =>
      sortJobs(
        filterJobs(jobs, {
          status,
          brandId: brandId || undefined,
          search,
          maxAgeDays: maxAgeDays || undefined,
        }),
      ),
    [jobs, status, brandId, search, maxAgeDays],
  );

  /*
   * Selection is kept to what is on screen. A filter change that hides a ticked
   * row must not leave it queued for a "Delete selected" the operator can no
   * longer see — the count in the button has to mean the rows in front of them.
   */
  const selectable = useMemo(() => visible.filter(isJobDeletable), [visible]);
  useEffect(() => {
    setSelected((prev) => {
      const stillVisible = new Set(selectable.map((j) => j.jobId));
      const next = new Set([...prev].filter((id) => stillVisible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectable]);

  const toggleSelected = (jobId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const allSelected = selectable.length > 0 && selected.size === selectable.length;

  const handleCancel = async (job: ExtractorJobDoc) => {
    setBusyJobId(job.jobId);
    try {
      await cancelExtractorJob(job.jobId);
      /*
       * No optimistic update: the runner polls `cancelRequested` every 5s and
       * exits cleanly, and the doc it writes on the way out is what the row
       * should show. Faking the terminal state here would claim the crawl had
       * stopped several seconds before it did.
       */
      toast.success('Cancellation requested');
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setBusyJobId(null);
    }
  };

  /**
   * Delete the rows the modal is holding.
   *
   * No optimistic removal: the live subscription is what owns this list, and a
   * row taken out here would reappear the instant a snapshot arrived if the
   * delete had in fact been refused.
   */
  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    const jobIds = pendingDelete.map((j) => j.jobId);
    setDeleting(true);
    try {
      const { deleted, skipped, storageErrors = [] } = await deleteExtractorJobs(jobIds);
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of deleted) next.delete(id);
        return next;
      });
      if (deleted.length > 0) {
        toast.success(`Deleted ${deleted.length} job${deleted.length === 1 ? '' : 's'}`);
      }
      for (const { jobId, reason } of skipped) {
        toast.error(`${jobId}: ${reason}`, { duration: 6000 });
      }
      for (const { jobId, error } of storageErrors) {
        toast.error(`${jobId}: row deleted, but its image folder was not — ${error}`, { duration: 8000 });
      }
      setPendingDelete(null);
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Close out a row the extractor is never going to close itself.
   *
   * No optimistic update, for the same reason cancel and delete do not take
   * one: the live subscription owns this list, and the write either lands and
   * arrives as a snapshot or was refused, in which case the row should not have
   * moved. The server re-checks staleness, so a job that revived while the tab
   * sat open comes back as a refusal rather than a closed crawl.
   */
  const handleForceCloseConfirmed = async () => {
    if (!pendingForceClose) return;
    setForceClosing(true);
    try {
      await forceCloseExtractorJob(pendingForceClose.jobId);
      toast.success('Job force-closed');
      setPendingForceClose(null);
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setForceClosing(false);
    }
  };

  const handleDownload = async (job: ExtractorJobDoc) => {
    setBusyJobId(job.jobId);
    try {
      const { result, bytes } = await getExtractorJobResult(job.jobId);
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${job.brandId ?? 'extraction'}-${job.jobId}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${(bytes / 1024).toFixed(0)} kB — import it from Import Sources`);
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setBusyJobId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Extractor Jobs</h2>
        <p className="mt-1 text-sm text-gray-500">
          Every scraper run, newest first, updating live. Start one from a brand&apos;s references
          page (Catalogue → Curated Watches → a brand → Run scraper); import a finished one
          straight into the bulk-import wizard from its row.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            <option value="active">Active (queued or running)</option>
            {JOB_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Brand</label>
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All brands</option>
            {brandIds.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Age</label>
          <select
            value={maxAgeDays}
            onChange={(e) => setMaxAgeDays(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            title="Hide older rows without deleting them — combine with Status to park, say, failed runs older than a week"
          >
            <option value={0}>Any age</option>
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Job id, brand, module, mode"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div className="text-sm text-gray-500 pb-2">
          {visible.length} of {jobs.length}
        </div>

        {selected.size > 0 && (
          <div className="pb-1">
            <Button
              size="sm"
              variant="danger"
              onClick={() => setPendingDelete(selectable.filter((j) => selected.has(j.jobId)))}
            >
              Delete selected ({selected.size})
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <div className="font-medium">Could not read the job history</div>
          <div className="mt-1 text-xs">{error}</div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading jobs…</div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          {jobs.length === 0
            ? 'No extraction jobs yet.'
            : 'No jobs match the current filters.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select every deletable job shown"
                    checked={allSelected}
                    disabled={selectable.length === 0}
                    onChange={() =>
                      setSelected(
                        allSelected ? new Set() : new Set(selectable.map((j) => j.jobId)),
                      )
                    }
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-2">Brand</th>
                <th className="px-4 py-2">Module / mode</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Started</th>
                <th className="px-4 py-2">Finished</th>
                <th className="px-4 py-2">Duration</th>
                <th className="px-4 py-2">Results</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((job) => {
                const count = jobResultCount(job);
                const snippet = jobErrorSnippet(job);
                const isOpen = expanded === job.jobId;
                const stuck = stuckJobReason(job, nowMs);
                return (
                  <Fragment key={job.jobId}>
                    <tr className="align-top">
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          aria-label={`Select job ${job.jobId}`}
                          checked={selected.has(job.jobId)}
                          disabled={!isJobDeletable(job)}
                          onChange={() => toggleSelected(job.jobId)}
                          className="rounded border-gray-300 disabled:opacity-40"
                        />
                      </td>
                      <td className="px-4 py-3">
                        {job.brandId && onOpenBrand ? (
                          <button
                            type="button"
                            onClick={() => onOpenBrand(job.brandId as string)}
                            className="text-brand-600 hover:text-brand-700 hover:underline"
                          >
                            {job.brandId}
                          </button>
                        ) : (
                          <span className="text-gray-900">{job.brandId ?? '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div>{job.extractorId ?? '—'}</div>
                        <div className="text-xs text-gray-400">{job.options?.mode ?? 'full'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusClasses(job.status)}`}
                        >
                          {job.status ?? 'unknown'}
                        </span>
                        {job.cancelRequested && job.status === 'running' && (
                          <div className="mt-1 text-[11px] text-amber-700">cancelling…</div>
                        )}
                        {stuck && (
                          <div className="mt-1 text-[11px] font-medium text-red-700">
                            stuck — no update from the extractor
                          </div>
                        )}
                        {/*
                          A force-closed row reads `cancelled` like any other, so
                          without this the history cannot distinguish a crawl the
                          operator stopped from one the extractor dropped.
                        */}
                        {job.status === 'cancelled' && job.endedBy === 'admin-force' && (
                          <div className="mt-1 text-[11px] text-gray-500">force-closed</div>
                        )}
                        {job.status === 'running' && job.progressLabel && (
                          <div className="mt-1 text-[11px] text-gray-500">{job.progressLabel}</div>
                        )}
                        {snippet && (
                          <div className="mt-1 text-[11px] text-red-700">{snippet}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatTime(job.startedAt ?? job.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatTime(job.completedAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDuration(jobDurationMs(job))}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {job.status === 'completed' ? (count ?? '—') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          {stuck && (
                            <button
                              type="button"
                              onClick={() => setPendingForceClose(job)}
                              className="text-xs font-medium text-red-600 hover:text-red-800 hover:underline"
                              title="Mark this job cancelled — the extractor is not contacted"
                            >
                              Force close
                            </button>
                          )}
                          {isCancellable(job) && (
                            <Button
                              size="sm"
                              variant="danger"
                              loading={busyJobId === job.jobId}
                              disabled={busyJobId === job.jobId}
                              onClick={() => void handleCancel(job)}
                            >
                              Cancel
                            </Button>
                          )}
                          {job.status === 'completed' && (
                            <>
                              <ImportJobResultButton
                                jobId={job.jobId}
                                brandId={job.brandId}
                                onImported={onImported}
                              />
                              <Button
                                size="sm"
                                variant="secondary"
                                loading={busyJobId === job.jobId}
                                disabled={busyJobId === job.jobId}
                                onClick={() => void handleDownload(job)}
                              >
                                Download JSON
                              </Button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => setExpanded(isOpen ? null : job.jobId)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            {isOpen ? 'Hide' : 'Details'}
                          </button>
                          {isJobDeletable(job) && (
                            <button
                              type="button"
                              onClick={() => setPendingDelete([job])}
                              className="text-xs text-gray-400 hover:text-red-600"
                              title="Delete this row from the history"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <div className="text-xs font-medium text-gray-600 mb-1">Job</div>
                              <dl className="text-xs text-gray-600 space-y-0.5">
                                <div>
                                  <span className="text-gray-400">id </span>
                                  <code>{job.jobId}</code>
                                </div>
                                <div>
                                  <span className="text-gray-400">created by </span>
                                  {job.createdBy ?? '—'}
                                  {job.triggeredFrom ? ` (${job.triggeredFrom})` : ''}
                                </div>
                                <div>
                                  <span className="text-gray-400">result path </span>
                                  <code className="break-all">{job.resultPath ?? '—'}</code>
                                </div>
                              </dl>
                              {job.resultPath && (
                                <p className="mt-1 text-[11px] text-gray-400">
                                  A path on the machine that ran the job, not a URL — use Result
                                  JSON to pull the envelope through the proxy.
                                </p>
                              )}
                            </div>
                            <div>
                              <div className="text-xs font-medium text-gray-600 mb-1">
                                Full document
                              </div>
                              <pre className="max-h-64 overflow-auto rounded bg-white border border-gray-200 p-2 text-[11px] text-gray-700">
                                {JSON.stringify(job, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={pendingForceClose !== null}
        onClose={() => (forceClosing ? undefined : setPendingForceClose(null))}
        title="Force close this job?"
        size="sm"
      >
        {pendingForceClose && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Job{' '}
              <code className="px-1 bg-gray-100 rounded text-xs">
                {pendingForceClose.jobId}
              </code>{' '}
              for{' '}
              <span className="font-medium">
                {pendingForceClose.brandId ?? 'an unknown brand'}
              </span>{' '}
              has been{' '}
              {stuckReasonLabel(stuckJobReason(pendingForceClose, nowMs) ?? 'running')} since{' '}
              {formatTime(
                pendingForceClose.updatedAt ??
                  pendingForceClose.startedAt ??
                  pendingForceClose.createdAt,
              )}
              .
            </p>

            <p className="text-xs text-gray-500">
              This marks the row <span className="font-medium">cancelled</span> so it stops
              blocking the history — the extractor is not contacted, and if a crawl somehow is
              still alive out there this will not stop it. Use Cancel instead while the extractor
              is reachable.
            </p>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <Button
                variant="secondary"
                onClick={() => setPendingForceClose(null)}
                disabled={forceClosing}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={forceClosing}
                disabled={forceClosing}
                onClick={() => void handleForceCloseConfirmed()}
              >
                Force close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={pendingDelete !== null}
        onClose={() => (deleting ? undefined : setPendingDelete(null))}
        title={
          pendingDelete && pendingDelete.length > 1
            ? `Delete ${pendingDelete.length} jobs?`
            : 'Delete job?'
        }
        size="sm"
      >
        <div className="space-y-4">
          {pendingDelete && pendingDelete.length === 1 && pendingDelete[0] ? (
            <p className="text-sm text-gray-700">
              Delete job{' '}
              <code className="px-1 bg-gray-100 rounded text-xs">{pendingDelete[0].jobId}</code> for{' '}
              <span className="font-medium">{pendingDelete[0].brandId ?? 'an unknown brand'}</span>?
            </p>
          ) : (
            <p className="text-sm text-gray-700">
              Delete {pendingDelete?.length ?? 0} job rows?
            </p>
          )}

          <p className="text-xs text-gray-500">
            This removes the row from history and deletes the job's downloaded images from Storage
            (references already imported keep their own copies). The extractor is not contacted;
            the result file stays on the machine that ran the crawl.
          </p>

          {pendingDelete && pendingDelete.length > 1 && (
            <ul className="max-h-40 overflow-y-auto rounded border border-gray-200 divide-y divide-gray-100 text-xs">
              {pendingDelete.map((job) => (
                <li key={job.jobId} className="px-2 py-1 flex justify-between gap-2">
                  <span className="font-mono text-gray-600 truncate">{job.jobId}</span>
                  <span className="text-gray-400 whitespace-nowrap">
                    {job.brandId ?? '—'} · {job.status ?? 'unknown'}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button
              variant="secondary"
              onClick={() => setPendingDelete(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleting}
              disabled={deleting}
              onClick={() => void handleDeleteConfirmed()}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
